/**
 * Modals.jsx
 *
 * All dialog overlays used by TeleprompterMain:
 *
 *   ScriptDiffModal – section-level diff when loading a new script
 *   ClipsModal      – recorded video clip management
 *   EditModal       – raw script text editor
 *   HelpModal       – keyboard-shortcut reference
 *   MobileDrawer    – bottom sheet for secondary controls on mobile/tablet
 */

import React, { useState, useEffect, useMemo } from "react";
import { CloseIcon, ChevronRightIcon, DlIcon, CheckIcon } from "./Overlays";
import {
  splitIntoSections,
  diffSections,
  parseWords,
  formatTime,
} from "./scriptUtils";

// ── ScriptDiffModal ───────────────────────────────────────────
export function ScriptDiffModal({ open, onClose, oldTokens, newRaw, onApplySections }) {
  const oldSections = useMemo(() => splitIntoSections(
    oldTokens.map(t => {
      if (t.type === "section") return `\n## ${t.label}\n`;
      if (t.type === "note")    return `[[${t.text}]]`;
      return t.word;
    }).join(" ").replace(/ \n/g, "\n").replace(/\n /g, "\n").trim()
  ), [oldTokens]);

  const newSections = useMemo(() => splitIntoSections(newRaw), [newRaw]);
  const diffed      = useMemo(() => diffSections(oldSections, newSections), [oldSections, newSections]);

  const [selected, setSelected] = useState(() => new Set(diffed.map((_, i) => i)));

  useEffect(() => {
    if (open) setSelected(new Set(diffed.map((_, i) => i)));
  }, [open, diffed]);

  const toggleSection = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const applySelected = () => {
    const parts = [];
    for (let i = 0; i < newSections.length; i++) {
      if (selected.has(i)) {
        const sec = diffed[i];
        if (sec.heading) parts.push(`## ${sec.heading}`);
        if (sec.body)    parts.push(sec.body);
      } else {
        const oldSec = oldSections.find(s => (s.heading ?? "__ROOT__") === (diffed[i].heading ?? "__ROOT__"));
        if (oldSec) {
          if (oldSec.heading) parts.push(`## ${oldSec.heading}`);
          if (oldSec.body)    parts.push(oldSec.body);
        }
      }
    }
    onApplySections(parts.join("\n\n"));
    onClose();
  };

  if (!open) return null;

  const statusColor = { new: "#66bb6a", changed: "#ffb74d", unchanged: "var(--muted)" };
  const statusLabel = { new: "NEW",     changed: "CHANGED", unchanged: "SAME" };

  return (
    <div
      className="tp-modal-overlay open"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="tp-modal" style={{ maxWidth: 560, maxHeight: "88vh", overflowY: "auto" }}>
        <div className="tp-modal-header">
          <span className="tp-modal-title">Update Script Sections</span>
          <button className="tp-modal-close" onClick={onClose} style={{display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><CloseIcon size={16}/></button>
        </div>

        <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'IBM Plex Mono',monospace", lineHeight: 1.7, padding: "0 2px" }}>
          Your new script has changed sections. Select which ones to apply — unchecked sections keep their current content.
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { label: "Select All",    color: "var(--muted)",  action: () => setSelected(new Set(diffed.map((_, i) => i))) },
            { label: "Changed Only",  color: "#ffb74d",       action: () => setSelected(new Set(diffed.map((s, i) => s.status !== "unchanged" ? i : -1).filter(i => i >= 0))) },
            { label: "None",          color: "var(--muted)",  action: () => setSelected(new Set()) },
          ].map(({ label, color, action }) => (
            <button
              key={label}
              onClick={action}
              style={{
                padding: "4px 12px", background: "var(--ctrl-bg)",
                border: "1px solid var(--border)", borderRadius: 6,
                color, fontFamily: "'IBM Plex Mono',monospace",
                fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 340, overflowY: "auto" }}>
          {diffed.map((sec, i) => {
            const checked    = selected.has(i);
            const wordCount  = parseWords(sec.body || "").length;
            return (
              <div
                key={i}
                onClick={() => toggleSection(i)}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  padding: "12px 14px", borderRadius: 9, cursor: "pointer",
                  background: checked ? "rgba(212,175,55,.05)" : "var(--ctrl-bg)",
                  border: `1px solid ${checked ? "rgba(212,175,55,.3)" : "var(--border)"}`,
                  transition: "all .12s",
                }}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: 4,
                  border: `2px solid ${checked ? "var(--g)" : "var(--border)"}`,
                  background: checked ? "var(--g)" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, marginTop: 1, transition: "all .12s",
                }}>
                  {checked && (
                    <svg viewBox="0 0 10 10" width={10} height={10}>
                      <polyline points="1.5,5 4,7.5 8.5,2" stroke="#0c0c0c" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: "var(--text)", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600 }}>
                      {sec.heading ? `## ${sec.heading}` : "(intro)"}
                    </span>
                    <span style={{
                      fontSize: 8, letterSpacing: ".15em", textTransform: "uppercase",
                      color: statusColor[sec.status], fontFamily: "'IBM Plex Mono',monospace",
                      padding: "1px 6px", border: `1px solid ${statusColor[sec.status]}55`, borderRadius: 3,
                    }}>
                      {statusLabel[sec.status]}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'IBM Plex Mono',monospace", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 60, overflow: "hidden" }}>
                    {sec.body?.slice(0, 120)}{(sec.body?.length ?? 0) > 120 ? "…" : ""}
                  </div>
                  <div style={{ fontSize: 9, color: "var(--muted)", fontFamily: "'IBM Plex Mono',monospace", marginTop: 4, opacity: .6 }}>
                    {wordCount} words
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="tp-modal-btns">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={applySelected} disabled={selected.size === 0}>
            Apply {selected.size} Section{selected.size !== 1 ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ClipsModal ────────────────────────────────────────────────
export function ClipsModal({ open, onClose, clips, onDownload, onDownloadAll, onDelete }) {
  return (
    <div
      className={`tp-modal-overlay${open ? " open" : ""}`}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="tp-modal" style={{ maxWidth: 480 }}>
        <div className="tp-modal-header">
          <span className="tp-modal-title">Recordings</span>
          <button className="tp-modal-close" onClick={onClose} style={{display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><CloseIcon size={16}/></button>
        </div>

        <div className="tp-clips-list">
          {clips.length === 0 ? (
            <div className="tp-clips-empty">No recordings yet</div>
          ) : (
            clips.map((clip, i) => (
              <div key={i} className="tp-clip-row">
                <div className="tp-clip-info">
                  <div className="tp-clip-name">{clip.name}</div>
                  <div className="tp-clip-meta">{formatTime(clip.duration)} · {clip.sizeMB} MB</div>
                </div>
                <div className="tp-clip-actions">
                  <button className="tp-clip-btn dl" onClick={() => onDownload(clip)} style={{display:"flex",alignItems:"center",gap:5}}><DlIcon size={12}/>Save</button>
                  <button className="tp-clip-btn"    onClick={() => onDelete(i)}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="tp-modal-btns">
          {clips.length > 0 && <button onClick={onDownloadAll}>Download All</button>}
          <button className="primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── EditModal ─────────────────────────────────────────────────
export function EditModal({
  open,
  onClose,
  draftScript,
  setDraftScript,
  setDraftCursor,
  textareaRef,
  onLoad,
}) {
  return (
    <div
      className={`tp-modal-overlay${open ? " open" : ""}`}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="tp-modal">
        <div className="tp-modal-header">
          <span className="tp-modal-title">Edit Script</span>
          <button className="tp-modal-close" onClick={onClose} style={{display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><CloseIcon size={16}/></button>
        </div>

        <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'IBM Plex Mono',monospace", letterSpacing: ".05em", lineHeight: 1.7, padding: "0 2px 2px" }}>
          <code style={{ background: "rgba(212,175,55,.1)", padding: "1px 5px", borderRadius: 3 }}>## Section Name</code>
          {" — adds a jump marker ·  "}
          <code style={{ background: "rgba(212,175,55,.1)", padding: "1px 5px", borderRadius: 3 }}>{"[[presenter note]]"}</code>
          {" — shows only to you"}
        </div>

        <textarea
          ref={textareaRef}
          value={draftScript}
          onChange={e => setDraftScript(e.target.value)}
          onSelect={e => setDraftCursor(e.target.selectionStart)}
          placeholder={"## Intro\nPaste your script here...\n\n[[Your presenter note here]]\n\n## Closing"}
        />

        <div className="tp-modal-btns">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={onLoad}>Load Script</button>
        </div>
      </div>
    </div>
  );
}

// ── HelpModal ─────────────────────────────────────────────────
// NOTE: Each <tr> is on one line with no whitespace text nodes between
// <tr> and <td> — this prevents the React hydration warning about
// whitespace text nodes as children of <tr>.
export function HelpModal({ open, onClose }) {
  return (
    <div
      className={`tp-modal-overlay${open ? " open" : ""}`}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="tp-modal" style={{ maxWidth: 460 }}>
        <div className="tp-modal-header">
          <span className="tp-modal-title">Keyboard Shortcuts</span>
          <button className="tp-modal-close" onClick={onClose} style={{display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><CloseIcon size={16}/></button>
        </div>

        <table className="tp-shortcuts-table">
          <tbody>
            <tr><td><span className="tp-kbd">Space</span></td><td>Play / Pause (3-2-1 countdown)</td></tr>
            <tr><td><span className="tp-kbd">Esc</span></td><td>Dismiss cue card / stop &amp; reset</td></tr>
            <tr><td><span className="tp-kbd">F</span></td><td>Toggle fullscreen</td></tr>
            <tr><td><span className="tp-kbd">→ ↓</span></td><td>Step forward one word</td></tr>
            <tr><td><span className="tp-kbd">← ↑</span></td><td>Step back one word</td></tr>
            <tr><td><span className="tp-kbd">?</span></td><td>Show this panel</td></tr>
          </tbody>
        </table>

        <p className="tp-shortcuts-note">
          Click any word to jump · Remote: open{" "}
          <code style={{ fontSize: 10 }}>/?remote-setup</code>{" "}
          on any device on the same network · QR pairing — no server needed
        </p>

        <div className="tp-modal-btns">
          <button className="primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// ── MobileDrawer ──────────────────────────────────────────────
/**
 * Bottom-sheet drawer shown on mobile/tablet (≤768px) containing
 * secondary controls that are hidden from the main footer on small screens.
 *
 * Props:
 *   open         – boolean
 *   onClose      – () => void
 *   children     – drawer content (control rows)
 */
export function MobileDrawer({ open, onClose, children }) {
  // Trap body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.7)",
          zIndex: 150,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          animation: "tp-overlay-in 200ms ease",
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 151,
          background: "var(--surface)",
          borderTop: "1px solid var(--border)",
          borderRadius: "16px 16px 0 0",
          padding: "0 0 env(safe-area-inset-bottom, 16px)",
          animation: "tp-drawer-in 260ms cubic-bezier(.34,1.2,.64,1)",
          maxHeight: "82dvh",
          overflowY: "auto",
          boxShadow: "0 -8px 40px rgba(0,0,0,.6)",
        }}
      >
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: "var(--border2)" }} />
        </div>

        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 20px 12px",
          borderBottom: "1px solid var(--border)",
        }}>
          <span style={{
            fontFamily: "'IBM Plex Mono',monospace",
            fontSize: 9,
            letterSpacing: ".22em",
            textTransform: "uppercase",
            color: "var(--muted)",
          }}>
            More Controls
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: 4,
              borderRadius: 6,
            }}
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "16px 20px 8px" }}>
          {children}
        </div>
      </div>

      <style>{`
        @keyframes tp-drawer-in {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}